import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { clearCacheService } from '../../services/services';
import { settingService } from '../../services/loginService';
import { updateSettingService } from '../../services/adminServices';

import { AppSetting, Setting } from 'MyAccountPlus-UIComponents';

export interface TooltipSection {
    section: string;
    tooltips: {
        [key: string]: string;
    }[];
}

export interface Tooltips {
    Overview: TooltipSection[];
    Payment: TooltipSection[];
    Products: TooltipSection[];
}

export interface OtherSettings extends Setting {
    payments: {
        unauth_payment?: boolean
    } & Setting["payments"];
    tooltips: Tooltips;
    register_message?: string;
    records?: {
        message?: string;
    }
    product?: {
        message?: string;
    }
    contact_us?: {
        message?: string;
        success_message?: string;
    }
    show_location?: boolean;
}

interface AppSettings extends AppSetting {
    setting: OtherSettings
}

const initialState: AppSettings = {
    loading: false,
    error: "",
    cache: "",
    success: "",
    setting: {
        promotional_images: {
            enabled: false,
            images: [],
            size: []
        },
        fuel_order_fill_only: false,
        workflows: [],
        show_account_application: false,
        customer_support_phone_number: '',
        support_text_messages: false,
        show_next_delivery: false,
        inquiry_types: [],
        feedback_email: null,
        website: '',
        recaptcha_key: '',
        live_chat_js_url: null,
        show_credit_application: false,
        notification_methods: [],
        copyright_notice: '',
        show_fuel_ordering: false,
        live_chat_url: null,
        site_email_name: '',
        customer_support_billing_email: null,
        full_site: '',
        show_site_name: false,
        logo: '',
        new_card_fee: 0,
        billing_address_required: false,
        show_select_paperless: false,
        show_contact_us: false,
        site_name: '',
        show_documents: false,
        relationship_to_owner: [],
        recaptcha_site_key: '',
        site_email_address: '',
        cc_convenience_fee_percent: 0,
        show_pricing: false,
        alert_options: [],
        customer_support_billing_phone: null,
        show_live_chat: false,
        payments: {
            auto_pay: false,
            customer_receipt: false,
            gateway: '',
            method: '',
            stored_payment_sources: false,
            types: [],
            guest_payment: false,
            amount_message: '',
            success_message: ''
        },
        service_area_states: [],
        service_area_zip_codes: [],
        tenant: '',
        tabs: [],
        tooltips: {
            Overview: [],
            Payment: [],
            Products: []
        }
    },
}

export const appSettingThunk = createAsyncThunk("appSetting/appSettingThunk", async () => {
    try {
        const { data } = await settingService();
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const clearCacheThunk = createAsyncThunk("appSetting/clearCacheThunk", async () => {
    try {
        const { data } = await clearCacheService();
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const updateSettingThunk = createAsyncThunk("appSetting/updateSettingThunk", async (args: Setting) => {
    try {
        const { data } = await updateSettingService(args);
        return { data }
    } catch (error: any) {
        console.log("error--", error)
        throw new Error(error)
    }
})

export const appSettingSlice = createSlice({
    name: 'appSetting',
    initialState,
    reducers: {
        errorReducer: (state) => {
            state.error = ""
        },
        clearCacheReducer: (state) => {
            state.cache = ""
        },
        successReducer: (state) => {
            state.success = ""
        },
        clearState: (state) => {
            return initialState
        }
    },
    extraReducers(builder) {
        builder
            .addCase(appSettingThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(appSettingThunk.fulfilled, (state, action) => {
                state.setting = action.payload.data
                localStorage.setItem("tenant", action.payload.data.tenant)
                state.loading = false
                state.error = ""
            })
            .addCase(appSettingThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(clearCacheThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(clearCacheThunk.fulfilled, (state, action) => {
                state.cache = action.payload.data
                state.loading = false
                state.error = ""
            })
            .addCase(clearCacheThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
            .addCase(updateSettingThunk.pending, (state, action) => {
                state.loading = true
            })
            .addCase(updateSettingThunk.fulfilled, (state, action) => {
                state.success = "App settings updated successfully!"
                state.setting = action.payload.data
                localStorage.setItem("tenant", action.payload.data.tenant)
                state.loading = false
                state.error = ""
            })
            .addCase(updateSettingThunk.rejected, (state, action) => {
                state.error = action.error.message!
                state.loading = false
            })
    },
})

export const appSettingActions = appSettingSlice.actions
export default appSettingSlice.reducer;